import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FlaskConical, Loader2, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-24">
        <div className="w-full max-w-sm space-y-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-sm bg-[#0A2463] flex items-center justify-center">
                <FlaskConical className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-black text-2xl tracking-tighter text-slate-950">MolecuAI</span>
            </div>
            <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Welcome back</h1>
            <p className="text-sm text-slate-500">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700" data-testid="login-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@molecuai.local"
                required
                data-testid="login-email-input"
                className="rounded-sm border-slate-200 focus:border-[#0A2463] focus:ring-1 focus:ring-[#0A2463]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="login-password-input"
                className="rounded-sm border-slate-200 focus:border-[#0A2463] focus:ring-1 focus:ring-[#0A2463]"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white font-medium active:scale-[0.98] transition-all"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-xs text-slate-400">
            AI-Powered Invoice Reconciliation Platform
          </p>

          <div className="mt-4 pt-3 border-t border-slate-100" data-testid="powered-by-login">
            <p className="text-[9px] uppercase tracking-[0.15em] text-slate-400 mb-2 text-center">Powered by Juno Labs, a Division of RTS</p>
            <div className="flex items-center justify-center gap-3">
              <img src="/junolabs-logo.png" alt="Juno Labs AI" className="h-8 object-contain" />
              <img src="/rts-logo.png" alt="Resolve Tech Solutions" className="h-6 object-contain" />
            </div>
          </div>
        </div>
      </div>

      {/* Right: Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/950241/pexels-photo-950241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
        />
        <div className="absolute inset-0 bg-slate-900/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <p className="text-white/90 font-heading text-xl font-bold tracking-tight">
            Transform a 2-5 day manual process into sub-4-hour automation
          </p>
          <p className="text-white/60 text-sm mt-2">
            Complete molecule-level cost transparency for LNG & Oil Gas midstream
          </p>
        </div>
      </div>
    </div>
  );
}
